import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react'
import { settingsApi } from '../api'
import { settingsErrorMessage } from '../errors'
import type {
  AudioDraft,
  AudioEndpointDraft,
  AudioKind,
  AudioSettings,
  ConnectionTest,
} from '../types'

function editable(settings: AudioSettings): AudioDraft {
  return {
    revision: settings.revision,
    transcription: { ...settings.transcription, api_key: '' },
    diarization: { ...settings.diarization, api_key: '' },
  }
}

const errorMessage = (error: unknown) =>
  settingsErrorMessage(error, 'Audio settings request failed.')

function useLoadAudioSettings(
  setDraft: Dispatch<SetStateAction<AudioDraft | null>>,
  report: (message: string) => void
) {
  useEffect(() => {
    let mounted = true
    settingsApi
      .getAudio()
      .then(settings => {
        if (mounted) setDraft(editable(settings))
      })
      .catch(error => {
        if (mounted) {
          report(errorMessage(error))
        }
      })
    return () => {
      mounted = false
    }
  }, [setDraft, report])
}

function useAudioConnectionTests(
  draft: AudioDraft | null,
  setBusy: Dispatch<SetStateAction<boolean>>
) {
  const [testResults, setTestResults] = useState<Record<AudioKind, ConnectionTest | null>>({
    transcription: null,
    diarization: null,
  })
  const [testingKind, setTestingKind] = useState<AudioKind | null>(null)
  const clear = (kind: AudioKind) => setTestResults(current => ({ ...current, [kind]: null }))
  const test = async (kind: AudioKind) => {
    if (!draft) return
    setBusy(true)
    setTestingKind(kind)
    clear(kind)
    try {
      const result = await settingsApi.testAudio(draft, kind)
      setTestResults(current => ({ ...current, [kind]: result }))
    } catch (error) {
      setTestResults(current => ({
        ...current,
        [kind]: { ok: false, message: errorMessage(error) },
      }))
    } finally {
      setBusy(false)
      setTestingKind(null)
    }
  }
  return { testResults, testingKind, test, clear }
}

export function useAudioSettings() {
  const [draft, setDraft] = useState<AudioDraft | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [failed, setFailed] = useState(false)
  const report = useCallback((message: string) => {
    setFailed(true)
    setMessage(message)
  }, [])
  useLoadAudioSettings(setDraft, report)
  const connectionTests = useAudioConnectionTests(draft, setBusy)
  const updateEndpoint = (kind: AudioKind, endpoint: AudioEndpointDraft) => {
    setDraft(current => (current ? { ...current, [kind]: endpoint } : current))
    connectionTests.clear(kind)
  }
  const save = async () => {
    if (!draft) return
    setBusy(true)
    setFailed(false)
    setMessage('')
    try {
      setDraft(editable(await settingsApi.saveAudio(draft)))
      setMessage(
        'Saved. New recordings use these settings; running recordings keep their original settings.'
      )
    } catch (error) {
      setFailed(true)
      setMessage(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return {
    draft,
    setDraft,
    updateEndpoint,
    busy,
    message,
    failed,
    save,
    test: connectionTests.test,
    testResults: connectionTests.testResults,
    testingKind: connectionTests.testingKind,
  }
}
